'use client';

import {
  createContext,
  useContext,
  useEffect,
  useSyncExternalStore,
  type PropsWithChildren,
} from 'react';

import { useTrackEvent } from '@/hooks/use-track-event';
import { NETWORK_ANALYTICS_EVENTS } from '@/lib/constants/post-message-types';
import { networkState } from '@/lib/utils/network-state';

type DebouncedNetworkState = {
  online: boolean;
  previous: boolean | undefined;
};

const DebouncedNetworkStateContext = createContext<DebouncedNetworkState | undefined>(undefined);

const defaultState: DebouncedNetworkState = { online: true, previous: undefined };

const subscribe = networkState.subscribe.bind(networkState);

function getSnapshot(): DebouncedNetworkState {
  return networkState.getState();
}

function getServerSnapshot(): DebouncedNetworkState {
  return defaultState;
}

export function DebouncedNetworkStateProvider({ children }: PropsWithChildren) {
  const trackEvent = useTrackEvent();

  useEffect(() => {
    networkState.init();
  }, []);

  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  useEffect(() => {
    if (state.previous === undefined) return;
    if (!networkState.shouldTrackEvent(state.online)) return;

    const offlineDurationMs = networkState.getOfflineDuration();

    if (!state.online) {
      trackEvent({ event: NETWORK_ANALYTICS_EVENTS.NETWORK_DISCONNECTED });
    } else if (offlineDurationMs !== null) {
      trackEvent({
        event: NETWORK_ANALYTICS_EVENTS.NETWORK_RECONNECTED,
        properties: { offlineDurationMs },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent reference changes shouldn't re-trigger
  }, [state.online, state.previous]);

  return (
    <DebouncedNetworkStateContext.Provider value={state}>
      {children}
    </DebouncedNetworkStateContext.Provider>
  );
}

export function useDebouncedNetworkState(): DebouncedNetworkState {
  const context = useContext(DebouncedNetworkStateContext);
  if (!context) {
    throw new Error('useDebouncedNetworkState must be used within DebouncedNetworkStateProvider');
  }
  return context;
}
